import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PHOTO_LABEL, TablePhotoOption } from '../../../config';
import { CheckIcon, CloseIcon } from '../../../../../components/IconButton/icons';
import './row-photo-picker.scss';

interface RowPhotoPickerProps {
  /** Finds the element the picker hangs from (the row's Photo cell). */
  anchorSelector: string;
  title: string;
  photos: TablePhotoOption[];
  /** The photos this row has. */
  selected: string[];
  /** Photos on another row, with where ("item 4"): ticking one moves it here. */
  elsewhere: Map<string, string>;
  onToggle: (imageId: string, linked: boolean) => void;
  onClear: () => void;
  onClose: () => void;
}

const WIDTH = 300;
const GAP = 4;

/**
 * Which photos a table row is about, ticked from a list of every photo in
 * the document. Rendered into document.body, outside the page: the table is
 * paginated on measured heights and the Pages rail clones the page DOM, and a
 * popover inside either would be measured or copied. It follows its anchor
 * (re-found by selector, since a row that moves to the next page remounts)
 * on scroll and resize, and closes on a press outside it or Escape -- which
 * goes no further, so it never also closes the editor.
 */
const RowPhotoPicker: React.FC<RowPhotoPickerProps> = ({
  anchorSelector,
  title,
  photos,
  selected,
  elsewhere,
  onToggle,
  onClear,
  onClose,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number; maxHeight: number } | null>(null);

  const place = useCallback(() => {
    const anchor = window.document.querySelector<HTMLElement>(anchorSelector);
    if (!anchor) {
      onClose();
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - GAP - 8;
    const above = rect.top - GAP - 8;
    const openUp = below < 240 && above > below;
    const maxHeight = Math.max(160, Math.min(420, openUp ? above : below));
    const height = Math.min(maxHeight, panelRef.current?.offsetHeight ?? maxHeight);
    setPosition({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - WIDTH - 8)),
      top: openUp ? rect.top - GAP - height : rect.bottom + GAP,
      maxHeight,
    });
  }, [anchorSelector, onClose]);

  useLayoutEffect(() => {
    place();
  }, [place, selected.length]);

  // Keyboard: focus moves into the picker when it opens.
  useEffect(() => {
    panelRef.current?.querySelector<HTMLElement>('.row-photo-picker__option, .row-photo-picker__done')?.focus();
  }, []);

  useEffect(() => {
    const onScroll = (e: Event) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      place();
    };
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (panelRef.current?.contains(target)) return;
      // The control that opened it toggles it itself.
      if (target?.closest?.('[data-photo-picker-trigger]')) return;
      onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      onClose();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', place);
    window.document.addEventListener('pointerdown', onPointerDown, true);
    window.document.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', place);
      window.document.removeEventListener('pointerdown', onPointerDown, true);
      window.document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [place, onClose]);

  return createPortal(
    <div
      ref={panelRef}
      className="row-photo-picker"
      role="dialog"
      aria-label={title}
      style={{
        left: position?.left ?? -9999,
        top: position?.top ?? -9999,
        width: WIDTH,
        maxHeight: position?.maxHeight,
      }}
    >
      <div className="row-photo-picker__head">
        <span className="row-photo-picker__title">{title}</span>
        <button type="button" className="row-photo-picker__close" aria-label="Close" title="Close" onClick={onClose}>
          <CloseIcon size={14} />
        </button>
      </div>
      {photos.length === 0 ? (
        <div className="row-photo-picker__empty">Add a photo appendix first.</div>
      ) : (
        <div className="row-photo-picker__list" role="group">
          {photos.map(photo => {
            const on = selected.includes(photo.id);
            const where = on ? null : elsewhere.get(photo.id) ?? null;
            return (
              <button
                key={photo.id}
                type="button"
                role="checkbox"
                aria-checked={on}
                className={`row-photo-picker__option${on ? ' is-on' : ''}`}
                title={where ? `${PHOTO_LABEL} ${photo.number} is on ${where}; ticking it moves it here` : undefined}
                onClick={() => onToggle(photo.id, !on)}
              >
                <span className="row-photo-picker__check" aria-hidden="true">
                  {on && <CheckIcon size={12} />}
                </span>
                <span className="row-photo-picker__thumb">
                  {photo.url && <img src={photo.url} alt="" />}
                </span>
                <span className="row-photo-picker__text">
                  <span className="row-photo-picker__label">{PHOTO_LABEL} {photo.number}</span>
                  {photo.caption && <span className="row-photo-picker__caption">{photo.caption}</span>}
                </span>
                {where && <span className="row-photo-picker__where">on {where}</span>}
              </button>
            );
          })}
        </div>
      )}
      <div className="row-photo-picker__foot">
        <button
          type="button"
          className="row-photo-picker__clear"
          disabled={selected.length === 0}
          title="Unlink every photo; the cell can then be typed in"
          onClick={onClear}
        >
          Clear photos
        </button>
        <button type="button" className="row-photo-picker__done" onClick={onClose}>
          Done
        </button>
      </div>
    </div>,
    window.document.body
  );
};

export default RowPhotoPicker;
